import React, { useState } from "react";
import { FileCheck2, FileX2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const field = "w-full h-9 px-2.5 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

export const INV_LABEL = { invoiced: "INVOICED", not_invoiced: "NOT INVOICED", pending: "INVOICE PENDING" };
export const INV_CLS = { invoiced: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]", not_invoiced: "bg-destructive/15 text-destructive", pending: "bg-amber-100 text-amber-800" };

export const invStatus = (g) => g.invoice_status || (g.invoice_no ? "invoiced" : "pending");

export const InvoiceBadge = ({ g }) => {
  const s = invStatus(g);
  if (s === "na") return null;
  return <span data-testid={`gp-inv-status-${g.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded w-fit ${INV_CLS[s]}`}>{INV_LABEL[s]}{s === "invoiced" && g.invoice_no ? ` · ${g.invoice_no}` : ""}</span>;
};

export const InvoiceMark = ({ g, canWrite, onSaved }) => {
  if (invStatus(g) === "na") return null;
  return <InvoiceMarkBox g={g} canWrite={canWrite} onSaved={onSaved} />;
};

const InvoiceMarkBox = ({ g, canWrite, onSaved }) => {
  const [mode, setMode] = useState("");
  const [no, setNo] = useState(g.invoice_no || "");
  const [remarks, setRemarks] = useState("");
  const pick = (m) => { setMode(m); setRemarks(""); setNo(m === "yes" ? g.invoice_no || "" : ""); };
  const save = async () => {
    try {
      const { data } = await api.post(`/gatepass/${g.id}/invoice-status`, { invoiced: mode === "yes", invoice_no: no, remarks });
      toast.success(mode === "yes" ? "Marked invoiced" : "Marked not invoiced");
      setMode(""); setRemarks(""); onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="gp-invoice-mark" className="rounded-md border border-border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">Invoice status</span>
        <InvoiceBadge g={g} />
      </div>
      {g.status !== "out" && invStatus(g) === "pending" && <p className="text-xs text-muted-foreground">Mark invoiced / not invoiced after the vehicle goes out.</p>}
      {canWrite && !mode && g.status === "out" && invStatus(g) === "pending" && <p data-testid="gp-invoice-due" className="text-xs font-semibold text-amber-700">Vehicle is out — please mark invoiced or not invoiced.</p>}
      {g.invoice_marked_by && <p className="text-xs text-muted-foreground">By {g.invoice_marked_by} · {fmt(g.invoice_marked_at)}{g.invoice_remarks ? ` · ${g.invoice_remarks}` : ""}</p>}
      {canWrite && !mode && g.status === "out" && (
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="gp-mark-invoiced" onClick={() => pick("yes")} className="h-9 rounded-md border border-input text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-muted transition-colors"><FileCheck2 size={14} /> Invoiced</button>
          <button data-testid="gp-mark-not-invoiced" onClick={() => pick("no")} className="h-9 rounded-md border border-input text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-muted transition-colors"><FileX2 size={14} /> Not invoiced</button>
        </div>
      )}
      {mode && (
        <div className="space-y-2">
          {mode === "yes" && <input data-testid="gp-mark-invoice-no" className={field + " num"} value={no} onChange={(e) => setNo(e.target.value.toUpperCase())} placeholder="Invoice number *" />}
          <input data-testid="gp-mark-remarks" className={field} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder={mode === "no" ? "Remarks: why not invoiced? *" : "Remarks (optional)"} />
          <div className="flex gap-2">
            <button data-testid="gp-mark-save" onClick={save} className="flex-1 h-9 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors">Save</button>
            <button onClick={() => setMode("")} className="h-9 px-3 rounded-md border border-input text-xs hover:bg-muted transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
